import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ShieldCheck, DeviceMobile, Camera, EnvelopeSimple, ChartLine, MagnifyingGlass,
    UserCircleCheck, ClockCounterClockwise, Lock, Cube, CheckCircle,
    Bell, ChartPie, GlobeHemisphereWest, Fingerprint, PaperPlaneTilt, LinkSimple,
    Broadcast, Certificate, ListChecks, Sliders, FileLock, ArrowRight,
} from "@phosphor-icons/react";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { SalesInquiryButton } from "../components/SalesInquiryDialog";

// ─── Reusable feature card ────────────────────────────────────────────────────
function FeatureBlock({ icon: Icon, tone, kicker, title, children, testId }) {
    const tones = {
        civic:   "bg-civic-50 border-civic-100 text-civic-700",
        emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
        rose:    "bg-rose-50 border-rose-100 text-rose-700",
        amber:   "bg-amber-50 border-amber-100 text-amber-700",
        violet:  "bg-violet-50 border-violet-100 text-violet-700",
        sky:     "bg-sky-50 border-sky-100 text-sky-700",
    };
    return (
        <motion.article
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.5 }}
            className="p-5 sm:p-6 rounded-lg border border-slate-200 bg-white h-full flex flex-col"
            data-testid={testId}
        >
            <div className="flex items-start gap-3">
                <div className={`h-11 w-11 rounded-md grid place-items-center border shrink-0 ${tones[tone] || tones.civic}`}>
                    <Icon size={22} weight="duotone" />
                </div>
                <div className="min-w-0">
                    <div className="overline text-slate-500 text-[10px]">{kicker}</div>
                    <h3 className="font-heading font-bold text-lg text-slate-900 leading-tight tracking-tight">{title}</h3>
                </div>
            </div>
            <div className="mt-3 text-sm text-slate-600 leading-relaxed space-y-2">
                {children}
            </div>
        </motion.article>
    );
}

// ─── Compact “as easy as 1-2-3” voter-flow step ──────────────────────────────
function FlowStep({ n, icon: Icon, title, body }) {
    return (
        <div className="flex gap-3">
            <div className="shrink-0">
                <div className="h-9 w-9 rounded-md grid place-items-center bg-civic-600 text-white font-heading font-bold text-sm">{n}</div>
            </div>
            <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                    <Icon size={15} weight="duotone" className="text-civic-600 shrink-0" />
                    <h4 className="font-semibold text-slate-900 text-sm">{title}</h4>
                </div>
                <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
            </div>
        </div>
    );
}

export default function Features() {
    return (
        <div className="min-h-screen bg-white">
            <Header />

            {/* ─── Hero ─────────────────────────────────────────────────────────────── */}
            <section className="relative border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 border border-civic-200 bg-civic-50 text-civic-700 px-2.5 py-0.5 rounded-sm overline text-[10px] mb-3"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse-soft" />
                        What's inside · features tour
                    </motion.div>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-slate-900 leading-[1.05]">
                        Every tool your election needs<br className="hidden sm:block" />
                        <span className="text-civic-600">in one auditable ledger.</span>
                    </h1>
                    <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-3xl leading-relaxed">
                        From a mobile-friendly ballot to biometric photo verification, from email announcements to instant results and per-voter hash receipts —
                        E-VoteVerify is a full-stack, blockchain-secured voting platform designed for elections of any size.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-2 sm:gap-3">
                        <SalesInquiryButton size="lg" testId="features-hero-sales-inquiry-btn" />
                        <Link to="/explorer"><Button size="lg" variant="outline"><Cube size={16} className="mr-2" /> See the ledger</Button></Link>
                    </div>
                </div>
            </section>

            {/* ─── Highlight strip ─────────────────────────────────────────────────── */}
            <section className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                        <DeviceMobile size={22} weight="duotone" className="text-civic-600 shrink-0" />
                        <div>
                            <div className="font-heading font-bold text-slate-900 text-sm">Vote from any device</div>
                            <div className="text-[11px] text-slate-500">Fully responsive · installable PWA</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Camera size={22} weight="duotone" className="text-rose-600 shrink-0" />
                        <div>
                            <div className="font-heading font-bold text-slate-900 text-sm">Secure elections</div>
                            <div className="text-[11px] text-slate-500">Photo capture on ballot cast</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <EnvelopeSimple size={22} weight="duotone" className="text-emerald-600 shrink-0" />
                        <div>
                            <div className="font-heading font-bold text-slate-900 text-sm">Blast emails</div>
                            <div className="text-[11px] text-slate-500">Welcome · reminders · results</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <ChartLine size={22} weight="duotone" className="text-amber-600 shrink-0" />
                        <div>
                            <div className="font-heading font-bold text-slate-900 text-sm">Instant results</div>
                            <div className="text-[11px] text-slate-500">Live tally on close</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Core features grid ──────────────────────────────────────────────── */}
            <section className="border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                    <div className="max-w-3xl">
                        <div className="overline text-civic-600">Platform features</div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 text-slate-900">Built for elections of every size and every device.</h2>
                        <p className="mt-3 text-slate-600 text-base leading-relaxed">
                            Whether you're running a state precinct, an HOA board, a union chapter, a family group or a school council — E-VoteVerify scales to fit,
                            without compromising the auditable, verifiable ledger under the hood.
                        </p>
                    </div>

                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        <FeatureBlock icon={DeviceMobile} tone="civic" kicker="Anywhere · any device" title="Vote from your pocket" testId="feat-mobile">
                            <p>
                                The full voter portal — sign-in, profile confirmation, ballot, receipt — is fully mobile-first. Every screen is designed for touch,
                                every button hits a 44px minimum tap target, and the app installs as a Progressive Web App so voters can pin it to their home screen
                                and cast a ballot offline-first once the assets are cached.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Works on iPhone, Android, tablet, laptop, kiosk</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Installable PWA with offline shell</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Zero app-store approval — link and go</li>
                            </ul>
                        </FeatureBlock>

                        <FeatureBlock icon={Camera} tone="rose" kicker="Secure elections" title="Photo &amp; biometric verification" testId="feat-secure">
                            <p>
                                Flag any election as <span className="font-semibold text-slate-900">Secure</span> and voters are required to snap a live selfie at the
                                moment their ballot is submitted. The photo is stored against the vote-log entry and rendered on the public "Who voted" list
                                (per your privacy policy) so observers can independently confirm the actual voter cast the actual ballot.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Live camera capture — no upload trickery</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Hardware detection: warns voters without a camera before they start</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Optional biometric roadmap for high-stakes elections</li>
                            </ul>
                        </FeatureBlock>

                        <FeatureBlock icon={EnvelopeSimple} tone="emerald" kicker="Get out the vote" title="Announcement &amp; reminder emails" testId="feat-emails">
                            <p>
                                Reach every eligible voter with a single click. Configure your SMTP once, then send:
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><Bell size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> <strong>Welcome</strong> emails when an election opens</li>
                                <li className="flex items-start gap-1.5"><Bell size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> <strong>Reminder</strong> blasts that skip anyone who has already voted</li>
                                <li className="flex items-start gap-1.5"><Bell size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> <strong>Results</strong> announcements when polls close</li>
                            </ul>
                            <p>
                                Every email carries a pre-filled login hotlink (<code className="hash-mono text-[11px] bg-slate-100 px-1 rounded">?voter=ID</code>)
                                so voters skip the retype. Delivery is reconciled against a per-election email history so any failures are surfaced to you, not lost.
                            </p>
                        </FeatureBlock>

                        <FeatureBlock icon={ChartLine} tone="amber" kicker="Real-time results" title="Publish tallies the moment polls close" testId="feat-results">
                            <p>
                                As soon as the election ends — or you close it early — results become live on a shareable public URL: <span className="hash-mono text-[11px]">/results/&lt;id&gt;</span>.
                                Bar charts and pie charts render every candidate and every ballot question, with turnout totals, per-precinct participation, and
                                cross-browser printable PDF output for certified copies.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><ChartPie size={13} weight="fill" className="text-amber-600 mt-0.5 shrink-0" /> Bar + pie charts sized by <em>actual</em> selections (multi-select safe)</li>
                                <li className="flex items-start gap-1.5"><Certificate size={13} weight="fill" className="text-amber-600 mt-0.5 shrink-0" /> Print-ready certified-results page (Chrome, Brave, Safari)</li>
                                <li className="flex items-start gap-1.5"><Sliders size={13} weight="fill" className="text-amber-600 mt-0.5 shrink-0" /> Toggle <em>Hide results</em> for private review before publishing</li>
                            </ul>
                        </FeatureBlock>

                        <FeatureBlock icon={LinkSimple} tone="violet" kicker="Every vote verifiable" title="Per-voter hash receipts" testId="feat-receipts">
                            <p>
                                When a voter casts their ballot, the ledger returns a SHA-256 receipt hash and block number. The voter can visit the public
                                <Link to="/audit" className="text-civic-600 underline-offset-2 hover:underline"> Audit page</Link> from any browser — no login required —
                                and paste that hash to confirm their vote is in the chain, exactly the way they cast it, without ever revealing <em>what</em> they voted for.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Cryptographic proof-of-inclusion in the chain</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Ballot choice remains sealed &amp; anonymous</li>
                                <li className="flex items-start gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" /> Explorer surface exposes every block for public inspection</li>
                            </ul>
                        </FeatureBlock>

                        <FeatureBlock icon={ShieldCheck} tone="sky" kicker="Trust by design" title="Blockchain-backed integrity" testId="feat-chain">
                            <p>
                                Every event — voter created, ballot cast, election closed, admin action — is chained on a simulated SHA-256 blockchain stored in MongoDB.
                                Any tamper attempt breaks the chain and is immediately visible on the public
                                <Link to="/explorer" className="text-civic-600 underline-offset-2 hover:underline"> Explorer</Link>.
                            </p>
                            <ul className="mt-2 space-y-1">
                                <li className="flex items-start gap-1.5"><Lock size={13} weight="fill" className="text-sky-600 mt-0.5 shrink-0" /> SHA-256 hash chain, bcrypt PIN hashing</li>
                                <li className="flex items-start gap-1.5"><FileLock size={13} weight="fill" className="text-sky-600 mt-0.5 shrink-0" /> Immutable admin audit trail — wipes, blasts, logins, all captured</li>
                                <li className="flex items-start gap-1.5"><Broadcast size={13} weight="fill" className="text-sky-600 mt-0.5 shrink-0" /> Optional roadmap: anchor block hashes to a real public chain</li>
                            </ul>
                        </FeatureBlock>
                    </div>
                </div>
            </section>

            {/* ─── Voter flow — "how it works" ─────────────────────────────────────── */}
            <section className="border-b border-slate-200 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                    <div className="max-w-2xl">
                        <div className="overline text-civic-600">How voting works</div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 text-slate-900">Four steps from tap to receipt.</h2>
                    </div>
                    <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                        <FlowStep n={1} icon={UserCircleCheck} title="Sign in" body="Voter enters their Voter ID (or email) and PIN. Case-insensitive, camera-capable check runs in the background before the ballot even loads." />
                        <FlowStep n={2} icon={ListChecks} title="Confirm profile &amp; ballot" body="Multi-step wizard walks the voter through profile confirmation, eligibility affirmation, and a multi-format ballot: single-choice, multi-select, rating, and free response." />
                        <FlowStep n={3} icon={Camera} title="Cast — with photo if required" body="For secure elections, a live selfie is captured at submission and stored against the vote-log entry. The ballot choice itself is never linked to the voter." />
                        <FlowStep n={4} icon={LinkSimple} title="Receive a hash receipt" body="The voter walks away with a SHA-256 receipt and block number. They can verify inclusion on the public Audit page from any device, any time." />
                    </div>
                </div>
            </section>

            {/* ─── Admin superpowers ──────────────────────────────────────────────── */}
            <section className="border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                    <div className="max-w-2xl">
                        <div className="overline text-civic-600">For the election authority</div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 text-slate-900">A control room that respects the vote.</h2>
                        <p className="mt-3 text-slate-600 text-base leading-relaxed">
                            The admin panel gives election officials everything they need to run a modern, credible election — without ever giving them the power to peek
                            at who voted for what.
                        </p>
                    </div>

                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                        <FeatureBlock icon={GlobeHemisphereWest} tone="civic" kicker="Elections" title="Elections at any scale" testId="feat-elections">
                            <p>
                                Create as many elections as you need — draft, active, closed. Restrict by precincts, schedule start / end windows,
                                auto-close on full turnout, archive after the fact.
                            </p>
                        </FeatureBlock>
                        <FeatureBlock icon={Fingerprint} tone="rose" kicker="Voter roll" title="Voter management &amp; PIN reset" testId="feat-voters">
                            <p>
                                Import a voter roll from CSV, geocode addresses against the US Census, mark active voters in green, dormant ones in red,
                                and issue self-service PIN resets over SMS or email.
                            </p>
                        </FeatureBlock>
                        <FeatureBlock icon={PaperPlaneTilt} tone="emerald" kicker="Communications" title="Bulk email over your own SMTP" testId="feat-smtp">
                            <p>
                                Bring your own SMTP (Gmail, SendGrid, Postmark, Mailgun). Preview every template before sending, retry failed deliveries,
                                and see a per-election email history for full accountability.
                            </p>
                        </FeatureBlock>
                        <FeatureBlock icon={ClockCounterClockwise} tone="amber" kicker="Audit &amp; safety" title="Immutable audit trail" testId="feat-audit">
                            <p>
                                Every super-admin action is captured on the chain: created / deleted voters, PIN resets, email blasts, backups, wipes.
                                Filter by kind, export as CSV, sign off on it.
                            </p>
                        </FeatureBlock>
                        <FeatureBlock icon={MagnifyingGlass} tone="violet" kicker="Impersonation" title="Test-vote as any voter — safely" testId="feat-impersonate">
                            <p>
                                Super-admins can impersonate any voter to run a dress rehearsal or reproduce a bug report. Every impersonated ballot is tagged
                                on the audit trail and the "Who voted" list.
                            </p>
                        </FeatureBlock>
                        <FeatureBlock icon={FileLock} tone="sky" kicker="Backups" title="Encrypted, scheduled backups" testId="feat-backups">
                            <p>
                                Nightly or weekly backup jobs emailed as JSON to a recovery address, plus a manual "download now" export.
                                Restore any snapshot in one click if disaster strikes.
                            </p>
                        </FeatureBlock>
                    </div>
                </div>
            </section>

            {/* ─── Closing CTA ─────────────────────────────────────────────────────── */}
            <section className="bg-civic-900 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14 grid lg:grid-cols-12 gap-6 items-center">
                    <div className="lg:col-span-8">
                        <div className="overline text-civic-200">Ready to run your election?</div>
                        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mt-2 break-words">Talk to us about licensing E-VoteVerify for your organization.</h2>
                        <p className="mt-2 text-sm text-slate-300 max-w-2xl">
                            Counties, HOAs, school boards, universities, unions, family groups — anyone with more than a handful of voters can license the platform.
                        </p>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
                        <SalesInquiryButton size="lg" testId="features-footer-sales-inquiry-btn" />
                        <Link to="/explorer"><Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-civic-900"><ArrowRight size={16} className="mr-2" /> See it live</Button></Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
